import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Divider,
    Grid,
    Paper,
    Skeleton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    useMediaQuery,
    IconButton,
} from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import style from "./HomePage.module.scss";

import ScoresImg from "./assets/scores.svg";
import InfoImage from "./assets/info.svg";
import { useMetersStore } from "../../../store/meters.store";
import { Link, useNavigate } from "react-router-dom";
import { MetersNames } from "../../../configs/meters.config";
import { usePaymentsStore } from "../../../store/payments.store";
import dayjs from "dayjs";
import { QRCodeSVG } from "qrcode.react";
import { Invoice, InvoiceInfo } from "../../../types/Store/PaymentsStore.types";
import { useAuthStore } from "../../../store/auth.store";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { enqueueSnackbar } from "notistack";
import { throwError } from "../../../hooks/useErrorHandler";
import { Loading } from "../../../utils/components/Loading";

// Helper to generate the last 12 months based on System Time
function getLast12Months() {
    const months = [
        "january",
        "february",
        "march",
        "april",
        "may",
        "june",
        "july",
        "august",
        "september",
        "october",
        "november",
        "december",
    ];

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonthIndex = currentDate.getMonth();

    const result = [];

    for (let i = 0; i < 12; i++) {
        // Calculate backwards from current month: 0 (current), 1 (last month), etc.
        const targetDate = new Date(
            currentYear,
            currentMonthIndex - i,
            1,
        );
        const year = targetDate.getFullYear();
        const monthIndex = targetDate.getMonth();

        result.push({
            monthName: months[monthIndex],
            year: year,
            key: `${months[monthIndex]}, ${year}`, // "january, 2026"
            displayKey: `${months[monthIndex]} ${year}`, // "january 2026"
        });
    }

    return result.reverse();
}

export function HomePage() {
    const navigate = useNavigate();
    const { getReadings, getMeters, readings } = useMetersStore();
    const {
        getLastPayments,
        getTransactions,
        getPeriodPayments,
        transactions,
        lastPayment,
        lastPaymentAmount,
        payments,
        balance,
        getInvoicePaymentInfo,
        getRecipientInfo,
    } = usePaymentsStore();
    const [loading, setLoading] = useState(false);

    const { accounts, activeAccount } = useAuthStore();

    const isSubmissionPeriod = dayjs().date() >= 10 && dayjs().date() <= 25;

    const activeAcc = accounts?.find((acc) => acc.id === activeAccount);

    const isMobile = useMediaQuery("(max-width: 1100px)");
    const isSmallMobile = useMediaQuery("(max-width: 700px)");

    useLayoutEffect(() => {
        (async () => {
            try {
                setLoading(true);
                await Promise.all([
                    getMeters(),
                    getReadings(1),
                    getLastPayments(),
                    getPeriodPayments(0),
                    getTransactions(0),
                ]);
            } catch (error) {
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const { chartXData, chartYData } = useMemo(() => {
        // Always generate timeline based on current date
        const timeline = getLast12Months();

        const xData = timeline.map((item) => {
            return dayjs(`${item.displayKey.split(" ").join(" 01 ")}`).format(
                "MMMM, YYYY",
            );
        });

        const yData = timeline.map((item) => {
            const payment = payments.find(
                (p) => p.month.toLowerCase() === item.key,
            );
            return payment ? payment.total_amount : 0;
        });

        return { chartXData: xData, chartYData: yData };
    }, [payments]);

    const transactionsData = useMemo(() => {
        return transactions.length > 12
            ? transactions.slice(0, 12)
            : transactions;
    }, [transactions]);

    const [dialogInfo, setDialogInfo] = useState<InvoiceInfo | null>();
    const [dialogInvoice, setDialogInvoice] = useState<Invoice | null>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogLoading, setDialogLoading] = useState(false);

    const handleOpenDialog = async (item: Invoice) => {
        try {
            setDialogLoading(true);
            setDialogInvoice(item);
            setDialogOpen(true);
            const invoceInfo = await getInvoicePaymentInfo(item.id);
            setDialogInfo(invoceInfo);
            setDialogLoading(false);
        } catch (error) {
            setDialogOpen(false);
            throwError(error);
        } finally {
            setDialogLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setDialogInfo(undefined);
        setDialogOpen(false);
    };

    const handleDownloadReceipt = async (invoice_id: string | number) => {
        enqueueSnackbar({
            message: "Загружаем квитанцию...",
            variant: "info",
        });
        const receiptURL = await getRecipientInfo(invoice_id);
        if (receiptURL) window.open(receiptURL, "_blank");
    };

    return (
        <div>
            {/* {JSON.stringify(organizations)} */}
            <h1 className={style.title}>Главная</h1>
            <Grid container spacing={2}>
                {/* Левая карточка — Оплата ЖКУ */}
                {!loading ? (
                    <>
                        <Grid size={{ xs: 12, sm: 12, md: 6, xl: 4 }}>
                            <Card
                                sx={{
                                    height: "100%",
                                    display: "flex", // Добавляем flex для Card
                                    flexDirection: "column", // Вертикальное направление
                                }}
                            >
                                <CardContent
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        flexGrow: 1, // Растягиваем на всю доступную высоту
                                        p: 3,
                                    }}
                                >
                                    {/* Верхняя часть с названием организации */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-start",
                                            marginBottom: 2,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="h5"
                                                fontWeight="bold"
                                            >
                                                Баланс
                                            </Typography>
                                            <Typography
                                                sx={{ fontSize: 15 }}
                                                color="text.secondary"
                                            >
                                                {dayjs()
                                                    .month(dayjs().month() - 1)
                                                    .format("MMMM YYYY")}
                                            </Typography>
                                        </Box>

                                        {/* <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{
                                                textTransform: "none",
                                            }}
                                            onClick={() => {
                                                handleDownloadReceipt(
                                                    invoice.id,
                                                );
                                            }}
                                        >
                                            Скачать квитанцию
                                        </Button> */}
                                    </Box>

                                    {/* Список платежей - будет растягиваться */}
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                py: 1,
                                                borderBottomWidth: 1,
                                                borderBottomStyle: "solid",
                                                borderBottomColor: "#e0e0e0",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {balance > 0 ? "Переплата" : "Задолженность"}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight="500"
                                            >
                                                {Math.abs(balance).toFixed(2)}
                                                ₽
                                            </Typography>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                ) : (
                    <Grid size={{ xs: 12, sm: 12, md: 6, xl: 4 }}>
                        <Card
                            sx={{
                                height: "100%",
                            }}
                        >
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        height: "39px",
                                        marginBottom: "13px",
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="h5"
                                            fontWeight="bold"
                                        >
                                            <Skeleton
                                                variant="text"
                                                width={150}
                                            />
                                        </Typography>
                                        <Typography
                                            sx={{ fontSize: 15 }}
                                            color="text.secondary"
                                        >
                                            <Skeleton
                                                variant="text"
                                                width={120}
                                            />
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ mt: 2 }}>
                                    {Array(6)
                                        .fill(null)
                                        .map((item, v) => (
                                            <Box
                                                key={v}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    my: 0.5,
                                                    borderBottomWidth: 1,
                                                    borderBottomStyle: "solid",
                                                    borderBottomColor:
                                                        "#e0e0e0",
                                                    paddingY: "8px",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    <Skeleton
                                                        variant="text"
                                                        width={100}
                                                    />
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="500"
                                                >
                                                    <Skeleton
                                                        variant="text"
                                                        width={100}
                                                    />
                                                </Typography>
                                            </Box>
                                        ))}
                                </Box>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="success"
                                    sx={{ mt: 2, borderRadius: 1 }}
                                >
                                    Оплатить ЖКУ
                                </Button>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mt: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <img src={InfoImage} /> Как быстро поступит
                                    мой платёж?
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
                {!loading ? (
                    <>
                        {lastPayment.map((invoice: Invoice) => {
                            return (
                                <Grid size={{ xs: 12, sm: 12, md: 6, xl: 4 }}>
                                    <Card
                                        sx={{
                                            height: "100%",
                                            display: "flex", // Добавляем flex для Card
                                            flexDirection: "column", // Вертикальное направление
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                flexGrow: 1, // Растягиваем на всю доступную высоту
                                                p: 3,
                                            }}
                                        >
                                            {/* Верхняя часть с названием организации */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    alignItems: "flex-start",
                                                    marginBottom: 2,
                                                }}
                                            >
                                                <Box>
                                                    <Typography
                                                        variant="h5"
                                                        fontWeight="bold"
                                                    >
                                                        {
                                                            invoice
                                                                .service_organization
                                                                .name
                                                        }
                                                    </Typography>
                                                    <Typography
                                                        sx={{ fontSize: 15 }}
                                                        color="text.secondary"
                                                    >
                                                        {dayjs()
                                                            .month(
                                                                dayjs().month() -
                                                                1,
                                                            )
                                                            .format(
                                                                "MMMM YYYY",
                                                            )}
                                                    </Typography>
                                                </Box>

                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        textTransform: "none",
                                                    }}
                                                    onClick={() => {
                                                        handleDownloadReceipt(
                                                            invoice.id,
                                                        );
                                                    }}
                                                >
                                                    Скачать квитанцию
                                                </Button>
                                            </Box>

                                            {/* Список платежей - будет растягиваться */}
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        py: 1,
                                                        borderBottomWidth: 1,
                                                        borderBottomStyle:
                                                            "solid",
                                                        borderBottomColor:
                                                            "#e0e0e0",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {
                                                            MetersNames[
                                                            invoice
                                                                .payment_type
                                                            ]
                                                        }
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="500"
                                                    >
                                                        {invoice.amount.toFixed(
                                                            2,
                                                        )}
                                                        ₽
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Кнопка оплаты - всегда внизу */}
                                            <Box sx={{ mt: "auto", pt: 2 }}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    color="success"
                                                    sx={{
                                                        borderRadius: 1,
                                                        // py: 1.5,
                                                    }}
                                                    onClick={() =>
                                                        handleOpenDialog(
                                                            invoice,
                                                        )
                                                    }
                                                >
                                                    Оплатить ЖКУ
                                                </Button>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </>
                ) : (
                    <Grid size={{ xs: 12, sm: 12, md: 6, xl: 4 }}>
                        <Card
                            sx={{
                                height: "100%",
                            }}
                        >
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        height: "39px",
                                        marginBottom: "13px",
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="h5"
                                            fontWeight="bold"
                                        >
                                            <Skeleton
                                                variant="text"
                                                width={150}
                                            />
                                        </Typography>
                                        <Typography
                                            sx={{ fontSize: 15 }}
                                            color="text.secondary"
                                        >
                                            <Skeleton
                                                variant="text"
                                                width={120}
                                            />
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ mt: 2 }}>
                                    {Array(6)
                                        .fill(null)
                                        .map((item, v) => (
                                            <Box
                                                key={v}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    my: 0.5,
                                                    borderBottomWidth: 1,
                                                    borderBottomStyle: "solid",
                                                    borderBottomColor:
                                                        "#e0e0e0",
                                                    paddingY: "8px",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    <Skeleton
                                                        variant="text"
                                                        width={100}
                                                    />
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="500"
                                                >
                                                    <Skeleton
                                                        variant="text"
                                                        width={100}
                                                    />
                                                </Typography>
                                            </Box>
                                        ))}
                                </Box>

                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="success"
                                    sx={{ mt: 2, borderRadius: 1 }}
                                >
                                    Оплатить ЖКУ
                                </Button>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mt: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <img src={InfoImage} /> Как быстро поступит
                                    мой платёж?
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
            <Grid container spacing={2} sx={{ marginTop: "16px" }}>
                {/* Средняя карточка — Счётчики */}
                {!loading ? (
                    <Grid size={{ xs: 12, sm: 12, md: 6, xl: 4 }}>
                        <Card
                            sx={{
                                minHeight: "100%",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <CardContent
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                    flex: 1,
                                }}
                            >
                                <Typography variant="h6" fontWeight="bold">
                                    Счётчики
                                </Typography>
                                <Typography color="text.secondary">
                                    {dayjs().format("MMMM YYYY")}
                                </Typography>

                                <Box sx={{ mt: 2 }}>
                                    {readings
                                        .filter(
                                            (n) => n?.meter?.is_active !== false
                                        )
                                        .map((n) => {
                                        return (
                                            <Box
                                                key={n?.meter?.id}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    mb: 1,
                                                    borderBottom:
                                                        "1px solid #e0e0e0",
                                                    paddingBottom: "8px",
                                                }}
                                            >
                                                <Typography>
                                                    {n?.meter?.title}
                                                </Typography>
                                                <Typography>
                                                    {n?.readings?.[0]?.value ??
                                                        0}{" "}
                                                    {n?.meter?.units}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>

                                <Box
                                    sx={{
                                        marginTop: "auto",
                                        p: 2,
                                        borderRadius: 1,
                                        border: "1px solid #ffd6d6",
                                        textAlign: "center",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            mb: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "16px",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <img src={ScoresImg} /> Передать показания
                                        можно с 10 по 25{" "}
                                        {dayjs().format("D MMMM").split(" ")[1]}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        sx={{
                                            textTransform: "none",
                                            width: "100%",
                                        }}
                                        onClick={() =>
                                            navigate("/admin/meters")
                                        }
                                        disabled={!isSubmissionPeriod}
                                    >
                                        {isSubmissionPeriod
                                            ? "Передать"
                                            : "Передача показаний доступна с 10 по 25 число"}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ) : (
                    <Grid size={{ xs: 12, sm: 12, md: 6, xl: 4 }}>
                        <Card
                            sx={{
                                minHeight: "100%",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            <CardContent
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    height: "100%",
                                    flex: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        height: "39px",
                                        marginBottom: "13px",
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="h5"
                                            fontWeight="bold"
                                        >
                                            <Skeleton
                                                variant="text"
                                                width={150}
                                            />
                                        </Typography>
                                        <Typography
                                            sx={{ fontSize: 15 }}
                                            color="text.secondary"
                                        >
                                            <Skeleton
                                                variant="text"
                                                width={120}
                                            />
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ mt: 2 }}>
                                    {Array(5)
                                        .fill(null)
                                        .map((item, v) => (
                                            <Box
                                                key={v}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    my: 0.5,
                                                    borderBottomWidth: 1,
                                                    borderBottomStyle: "solid",
                                                    borderBottomColor:
                                                        "#e0e0e0",
                                                    paddingY: "8px",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    <Skeleton
                                                        variant="text"
                                                        width={100}
                                                    />
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="500"
                                                >
                                                    <Skeleton
                                                        variant="text"
                                                        width={100}
                                                    />
                                                </Typography>
                                            </Box>
                                        ))}
                                </Box>

                                <Box
                                    sx={{
                                        marginTop: "auto",
                                        p: 2,
                                        borderRadius: 1,
                                        border: "1px solid #ffd6d6",
                                        textAlign: "center",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            mb: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "16px",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <img src={ScoresImg} /> Передать показания
                                        можно с 10 по 25{" "}
                                        {dayjs().format("D MMMM").split(" ")[1]}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        sx={{
                                            textTransform: "none",
                                            width: "100%",
                                        }}
                                        onClick={() =>
                                            navigate("/admin/meters")
                                        }
                                        disabled={!isSubmissionPeriod}
                                    >
                                        {isSubmissionPeriod
                                            ? "Передать"
                                            : "Передача показаний доступна с 10 по 25 число"}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Правая карточка — История платежей */}
                {!loading ? (
                    <Grid size={{ xs: 12, sm: 12, md: 6, xl: 4 }}>
                        <Card sx={{ minHeight: "100%" }}>
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography variant="h6" fontWeight="bold">
                                        История платежей
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{ textTransform: "none" }}
                                        component={Link}
                                        to={"/admin/transactions"}
                                    >
                                        Подробнее
                                    </Button>
                                </Box>
                                <Typography
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                >
                                    {dayjs(
                                        transactionsData[
                                            transactionsData.length - 1
                                        ]?.created_at,
                                    ).format("MMMM")}
                                    {" – "}
                                    {dayjs(
                                        transactionsData[0]?.created_at,
                                    ).format("MMMM YYYY")}
                                </Typography>

                                {transactionsData.map((item) => (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            my: 0.5,
                                            paddingY: "8px",
                                            borderBottom: "1px solid #e0e0e0",
                                        }}
                                    >
                                        <Typography>
                                            {dayjs(
                                                item?.created_at || Date.now(),
                                            ).format("MMMM")}
                                        </Typography>
                                        <Typography>
                                            {item.amount.toFixed(2)}₽
                                        </Typography>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card>
                    </Grid>
                ) : (
                    <Grid size={{ xs: 12, sm: 6, md: 6, xl: 4 }}>
                        <Card sx={{ minHeight: "100%" }}>
                            <CardContent>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        height: "39px",
                                        marginBottom: "13px",
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="h5"
                                            fontWeight="bold"
                                        >
                                            <Skeleton
                                                variant="text"
                                                width={150}
                                            />
                                        </Typography>
                                        <Typography
                                            sx={{ fontSize: 15 }}
                                            color="text.secondary"
                                        >
                                            <Skeleton
                                                variant="text"
                                                width={120}
                                            />
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ mt: 2 }}>
                                    {Array(8)
                                        .fill(null)
                                        .map((item, v) => (
                                            <Box
                                                key={v}
                                                sx={{
                                                    display: "flex",
                                                    justifyContent:
                                                        "space-between",
                                                    my: 0.5,
                                                    borderBottomWidth: 1,
                                                    borderBottomStyle: "solid",
                                                    borderBottomColor:
                                                        "#e0e0e0",
                                                    paddingY: "8px",
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    <Skeleton
                                                        variant="text"
                                                        width={100}
                                                    />
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="500"
                                                >
                                                    <Skeleton
                                                        variant="text"
                                                        width={100}
                                                    />
                                                </Typography>
                                            </Box>
                                        ))}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
            {payments.length > 0 && (
                <Paper sx={{ padding: "16px 32px", marginTop: "28px" }}>
                    <h2>История по счету</h2>
                    <p>За последние 12 месяцев</p>
                    {!loading ? (
                        <Box sx={{ width: "100%" }}>
                            <LineChart
                                sx={
                                    {
                                        // чуть больше воздуха + аккуратные сетки
                                        // "--Charts-axis-overflow": "visible",
                                    }
                                }
                                height={360}
                                experimentalFeatures={{
                                    preferStrictDomainInLineCharts: true,
                                }}
                                xAxis={[
                                    {
                                        data: chartXData,
                                        label: "Месяц, год",
                                        scaleType: "band",
                                    },
                                ]}
                                yAxis={[
                                    {
                                        label: "Сумма",
                                        width: 60,
                                        domainLimit: (min, max) => ({
                                            min: -(max / 2),
                                            max: (max * 1.1),
                                        }),
                                    },
                                ]}
                                series={[
                                    {
                                        label: "Сумма",
                                        data: chartYData,
                                        curve: "catmullRom", // плавная линия
                                    },
                                ]}
                                hideLegend
                            />
                        </Box>
                    ) : (
                        <Skeleton variant="rectangular" height={360} />
                    )}
                </Paper>
            )}

            <Dialog open={dialogOpen}>
                <DialogTitle>Оплата</DialogTitle>
                {dialogLoading ? (
                    <Box
                        sx={{
                            height: "200px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Loading />
                    </Box>
                ) : (
                    <>
                        <DialogContent>
                            {isSmallMobile ? (
                                <>
                                    <Typography>
                                        Оплатите ЖКУ компании{" "}
                                        {
                                            dialogInvoice?.service_organization
                                                .name
                                        }{" "}
                                        в размере{" "}
                                        {dialogInvoice?.amount.toFixed(2)}{" "}
                                        рублей.
                                    </Typography>
                                    <Typography>
                                        Для оплаты испольуйзте инструкцию ниже:
                                    </Typography>
                                    <Typography>
                                        Cкопируйте лицевой счёт:{" "}
                                        <span style={{ color: "#777" }}>
                                            {activeAcc?.external_id}
                                        </span>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                try {
                                                    navigator.clipboard
                                                        .writeText(
                                                            activeAcc?.external_id ??
                                                            "",
                                                        )
                                                        .then(() => {
                                                            enqueueSnackbar({
                                                                message:
                                                                    "Текст скопирован",
                                                                variant:
                                                                    "success",
                                                            });
                                                        })
                                                        .catch((err) => {
                                                            enqueueSnackbar({
                                                                message:
                                                                    "Текст не скопирован",
                                                                variant:
                                                                    "error",
                                                            });
                                                        });
                                                } catch (error) {
                                                    console.log(error);
                                                    enqueueSnackbar({
                                                        message:
                                                            "Текст не скопирован",
                                                        variant: "error",
                                                    });
                                                }
                                            }}
                                        >
                                            <ContentCopyIcon />
                                        </IconButton>
                                    </Typography>
                                    <Typography>
                                        Для оплаты нажмите на кнопку "Оплатить"
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography>
                                        Для оплаты воспользуйтесь QR кодом:
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            marginTop: "30px",
                                        }}
                                    >
                                        {dialogInfo?.qr_data && (
                                            <QRCodeSVG
                                                value={dialogInfo.qr_data}
                                                size={256}
                                            />
                                        )}
                                    </Box>
                                </>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog}>Отмена</Button>
                            {isSmallMobile && (
                                <Button
                                    href={dialogInfo?.payment_link || ""}
                                    target="_blank"
                                >
                                    Оплатить
                                </Button>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </div>
    );
}
